import { z } from 'zod';

/**
 * Schema Zod do form de Taxa.
 *
 * Modelo do form:
 * - Decimais monetários trafegam como **string** no payload final (padrão GeneXus
 *   p/ preservar precisão), mas a UI usa `number` no input — schema aceita
 *   `number` e o consumidor converte com `toFixed(2)` antes de enviar.
 * - `dataVigencia` ISO `YYYY-MM-DD` (DatePicker).
 * - Validações (memória `colaborador-taxa-valor-hora-em-fixo`):
 *   - `valorHora*` > 0 SEMPRE (mesmo em F — usado em horas excedentes).
 *   - `valorFixo*` > 0 SOMENTE quando `tipoPagamento='F'`.
 */

/** Decimal monetário ≥ 0 com até 2 casas. Usado em `valorHora*` (modo número na UI). */
const decimalMonetarioNonNegativo = z
  .number()
  .min(0, 'Valor mínimo 0')
  .refine((n) => Number.isFinite(n) && Math.round(n * 100) === n * 100, {
    message: 'Máximo 2 casas decimais',
  });

export const employeeTaxaInputSchema = z
  .object({
    dataVigencia: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (use AAAA-MM-DD)'),
    tipoPagamento: z.enum(['H', 'F']),
    valorHoraPagar: decimalMonetarioNonNegativo,
    valorHoraReceber: decimalMonetarioNonNegativo,
    valorFixoPagar: decimalMonetarioNonNegativo,
    valorFixoReceber: decimalMonetarioNonNegativo,
  })
  .superRefine((v, ctx) => {
    // Regra de negócio (Claudio 2026-06-07): valorHora* obrigatório SEMPRE.
    if (v.valorHoraPagar <= 0) {
      ctx.addIssue({
        path: ['valorHoraPagar'],
        code: 'custom',
        message: 'Valor hora a pagar é obrigatório (usado em horas excedentes).',
      });
    }
    if (v.valorHoraReceber <= 0) {
      ctx.addIssue({
        path: ['valorHoraReceber'],
        code: 'custom',
        message: 'Valor hora a receber é obrigatório (usado em horas excedentes).',
      });
    }
    // Em F, valorFixo* também obrigatório.
    if (v.tipoPagamento === 'F') {
      if (v.valorFixoPagar <= 0) {
        ctx.addIssue({
          path: ['valorFixoPagar'],
          code: 'custom',
          message: 'Valor fixo a pagar é obrigatório quando o tipo é Fixo.',
        });
      }
      if (v.valorFixoReceber <= 0) {
        ctx.addIssue({
          path: ['valorFixoReceber'],
          code: 'custom',
          message: 'Valor fixo a receber é obrigatório quando o tipo é Fixo.',
        });
      }
    }
  });

export type EmployeeTaxaFormValues = z.infer<typeof employeeTaxaInputSchema>;
